/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useContext, useMemo } from "react";
import { useTranslate } from "@adapptio/lib-components";
import {
	TGetBlueprintSchemaModel,
	TSchemaEventHandler,
	TSchemaEventsModel
} from "@adapptio/lib-blueprint";
import { v4 as uuid_v4 } from "uuid";

import { useBlueprintModel } from "../../hooks/useBlueprintModel";
import { InvalidModelNode } from "./InvalidModelNode";
import { IInspectorControlProps } from "./InspectorTypes";
import { FIELD_NOTE_ALIGN, FieldNote } from "../Fields/FieldNote";
import { IDropdownMenuItem } from "../DropdownMenu";
import { CoreComponentEventNodeTypes } from "@adapptio/lib-core";
import { EditorInspectorContext, TInspectEventMapSelectFn } from "../../contexts/EditorInspectorContext";
import {
	FieldList,
	FieldListItem,
	FieldListItemAction,
	FieldListItemHeader,
	FieldRow,
	FieldSection,
	Icon,
	ICON_SIZE
} from "@adapptio/lib-editor-ui";

export interface IInspectEventMapProps extends IInspectorControlProps<TSchemaEventsModel> {}

export const InspectEventMap: FC<IInspectEventMapProps> = ({ model: _model, depth }) => {
	const { selectedEventName, onEventSelect, onEventUnselect } = useContext(EditorInspectorContext);
	const { model } = useBlueprintModel(_model, "constObject");
	const t = useTranslate();

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const eventNames = Object.keys(model.props);

	if (!eventNames?.length) {
		return (
			<FieldNote align={FIELD_NOTE_ALIGN.CENTER} padded={true}>
				<span>{t("editor", "No events available.")}</span>
			</FieldNote>
		);
	}

	const childs = eventNames.map((eventName) => {
		return (
			<EventMapItem
				key={eventName}
				eventName={eventName}
				model={model.props[eventName]}
				depth={depth}
				isSelected={selectedEventName === eventName}
				onEventSelect={onEventSelect}
				onEventUnselect={onEventUnselect}
			/>
		);
	});

	return (
		<FieldSection stateKey="events" upperMargin>
			<FieldRow>
				<FieldList>{childs}</FieldList>
			</FieldRow>
		</FieldSection>
	);
};

interface IEventMapItemProps {
	eventName: string;
	model: TGetBlueprintSchemaModel<TSchemaEventHandler>;
	depth: number;
	isSelected: boolean;
	onEventSelect: TInspectEventMapSelectFn;
	onEventUnselect: TInspectEventMapSelectFn;
}

const EventMapItem: FC<IEventMapItemProps> = ({
	eventName,
	model: _model,
	isSelected,
	onEventSelect,
	onEventUnselect
}) => {
	const t = useTranslate();
	const { model } = useBlueprintModel(_model);

	// Watch event nodes as well
	const { model: nodeListModel, updateRef: nodeListUpdateRef } = useBlueprintModel(
		model?.props.nodes.template,
		"flowNodeList"
	);

	const itemActions = useMemo(() => {
		if (!model || !nodeListModel) {
			return [];
		}

		if (nodeListModel.items.length < 2) {
			const addNodeMenu: IDropdownMenuItem[] = [];

			addNodeMenu.push({
				id: "custom",
				label: "Add custom logic",
				icon: "mdi/help-rhombus",
				onClick: () => onEventSelect(eventName, model)
			});

			const nodeTypesEntries = Object.entries(CoreComponentEventNodeTypes);

			for (let i = 0; i < nodeTypesEntries.length; i++) {
				const [ id, nodeType ] = nodeTypesEntries[i];

				if (nodeType.editorOptions?.displayInQuickMenu === true) {
					addNodeMenu.push({
						id: id,
						label: nodeType.label,
						icon: nodeType.icon,
						onClick: () => {
							const newNode = nodeListModel.schema.addItem(
								nodeListModel,
								{
									id: uuid_v4(),
									varName: id,
									type: id,
									opts: {},
									outputs: {},
									position: {
										x: 100,
										y: 0
									},
									comment: ''
								},
								true
							);

							const entryNode = nodeListModel.schema.getEntryNode(nodeListModel);

							if (entryNode) {
								entryNode.schema.connectOutput(entryNode, "onEvent", newNode.id, true);
							}

							onEventSelect(eventName, model, newNode);
						}
					});
				}
			}

			return [
				{
					id: "add",
					icon: "mdi/plus",
					tooltip: "Add Event Logic",
					options: addNodeMenu
				}
			];
		}

		return [
			{
				id: "configure",
				icon: "mdi/share-variant-outline",
				tooltip: "Configure Event Logic",
				onClick: () => onEventSelect(eventName, model)
			},
			{
				id: "remove",
				icon: "mdi/close",
				tooltip: "Remove Event Logic",
				onClick: () => {
					nodeListModel.schema.removeAllItems(nodeListModel, true);
					onEventUnselect(eventName, model);
				}
			}
		];
	}, [ eventName, model, nodeListModel, nodeListUpdateRef ]);

	const handleClick = useCallback(() => {
		if (isSelected) {
			onEventUnselect(eventName, _model);
		} else {
			onEventSelect(eventName, _model);
		}
	}, [ eventName, _model, isSelected ]);

	const label = model.schema.opts.label ? t("editor", model.schema.opts.label) : eventName;
	const icon = model.schema.opts.icon || "mdi/lightning-bolt";

	/*
	actions={itemActions}*/

	const secondaryAction = (
		<>
			{itemActions.map((action) => (
				<FieldListItemAction
					key={action.id}
					options={action.options}
					tooltip={action.tooltip}
					onClick={action.onClick}
				>
					<Icon name={action.icon} />
				</FieldListItemAction>
			))}
		</>
	);

	return (
		<FieldListItem mark={"event-" + eventName}>
			<FieldListItemHeader
				onClick={handleClick}
				selected={isSelected}
				secondaryAction={secondaryAction}
			>
				<Icon name={icon} size={ICON_SIZE.XSMALL} style={{ margin: "0 4px" }} />
				{label}
			</FieldListItemHeader>
		</FieldListItem>
	);
};
